import type { JSX, ReactNode } from 'react'
import { TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/primitives/Button'
import { Surface } from '@/ui/primitives/Surface'
import { problemText } from './problem'

/**
 * Три состояния, которые обязаны быть у каждого экрана: загрузка, пусто, ошибка.
 *
 * Они собраны в один модуль не ради экономии строк, а чтобы форма скелетона и
 * форма контента задавались ОДНИМИ И ТЕМИ ЖЕ классами сетки: скелетон, у
 * которого своя вёрстка, гарантированно «прыгает» при появлении данных.
 */

export interface EmptyBlockProps {
  title: string
  note: string
  /** Ровно одно действие: список из трёх кнопок на пустом экране — не подсказка. */
  action?: { label: string; onClick: () => void; disabled?: boolean; title?: string }
  children?: ReactNode
}

export function EmptyBlock({ title, note, action, children }: EmptyBlockProps): JSX.Element {
  return (
    <Surface variant="raised" className="px-6 py-12 text-center">
      <p className="font-display text-md font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-1 max-w-prose text-base text-ink-muted">{note}</p>
      {action === undefined ? null : (
        <Button
          variant="primary"
          className="mt-5"
          disabled={action.disabled === true}
          title={action.title}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
      {children}
    </Surface>
  )
}

/** Ошибка загрузки: текст причины плюс кнопка повтора — без «что-то пошло не так». */
export function ErrorBlock({
  error,
  onRetry,
  title = 'Не удалось загрузить данные'
}: {
  error: unknown
  onRetry: () => void
  title?: string
}): JSX.Element {
  return (
    <Surface variant="raised" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      <span
        aria-hidden
        className="flex size-10 items-center justify-center rounded-pill bg-fail/12 text-fail"
      >
        <TriangleAlert className="size-5" />
      </span>
      <div>
        <p className="font-display text-md font-semibold text-ink">{title}</p>
        {/* role="alert" именно на строке с причиной: заголовок статичен, а
            меняется и должна быть озвучена именно причина. */}
        <p role="alert" className="mx-auto mt-1 max-w-prose text-base text-ink-muted">
          {problemText(error)}
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Повторить
      </Button>
    </Surface>
  )
}

/** Скелетон-плитка: та же поверхность и та же высота, что у настоящей карточки. */
export function SkeletonGrid({
  count = 6,
  className,
  tileClassName
}: {
  count?: number
  /** Классы сетки — те же, что у списка с данными. */
  className: string
  /** Высота плитки — под высоту настоящей карточки экрана. */
  tileClassName: string
}): JSX.Element {
  return (
    <div className={className}>
      {Array.from({ length: count }, (_unused, index) => (
        <Surface key={index} variant="raised" className={cn('shimmer', tileClassName)} aria-hidden>
          <span className="sr-only">Загрузка</span>
        </Surface>
      ))}
    </div>
  )
}
